import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Public Pages
import HomePage from "./pages/HomePage";
import HowItWorks from "./pages/HowItWorks";
import FAQ from "./pages/FAQ";
import Safety from "./pages/Safety";
import Pricing from "./pages/Pricing";
import SignUp from "./pages/SignUp";
import Login from "./pages/Login";

// User Pages
import Dashboard from "./pages/Dashboard";
import ApplicationForm from "./pages/ApplicationForm";
import VideoCall from "./pages/VideoCall";
import FeedbackForm from "./pages/FeedbackForm";
import ForgotPassword from "./pages/ForgotPassword";
import RefundPolicy from "./pages/RefundPolicy";
import TermsOfService from "./pages/TermsOfService";
import AntiStalkingPolicy from "./pages/AntiStalkingPolicy";
import BioMission from "./pages/BioMission";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminApplications from "./pages/admin/AdminApplications";
import AdminMatching from "./pages/admin/AdminMatching";
import AdminUserDetail from "./pages/admin/AdminUserDetail";
import AdminUserDatabase from "./pages/admin/AdminUserDatabase";
import AdminAccount from "./pages/admin/AdminAccount";
import AdminTeam from "./pages/admin/AdminTeam";

import "./App.css";

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (adminOnly && !user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Public Route (redirects if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to={user.is_admin ? "/admin" : "/dashboard"} replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/safety" element={<Safety />} />
      <Route path="/bio-mission" element={<BioMission />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/signup" element={<PublicRoute><SignUp /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/privacy-policy" element={<AntiStalkingPolicy />} />
      <Route path="/anti-stalking-policy" element={<AntiStalkingPolicy />} />
      
      {/* User Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/application" element={<ProtectedRoute><ApplicationForm /></ProtectedRoute>} />
      <Route path="/video-call/:matchId" element={<ProtectedRoute><VideoCall /></ProtectedRoute>} />
      <Route path="/feedback/:matchId" element={<ProtectedRoute><FeedbackForm /></ProtectedRoute>} />
      
      {/* Admin Routes */}
      <Route path="/admin/login" element={<Navigate to="/login" replace />} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/applications" element={<ProtectedRoute adminOnly><AdminApplications /></ProtectedRoute>} />
      <Route path="/admin/database" element={<ProtectedRoute adminOnly><AdminUserDatabase /></ProtectedRoute>} />
      <Route path="/admin/matching" element={<ProtectedRoute adminOnly><AdminMatching /></ProtectedRoute>} />
      <Route path="/admin/user/:userId" element={<ProtectedRoute adminOnly><AdminUserDetail /></ProtectedRoute>} />
      <Route path="/admin/account" element={<ProtectedRoute adminOnly><AdminAccount /></ProtectedRoute>} />
      <Route path="/admin/team" element={<ProtectedRoute adminOnly><AdminTeam /></ProtectedRoute>} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollToTop />
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
